import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { CalendarEvent } from '../types/CalendarEvent';
import { format } from 'date-fns';
import ConfirmModal from './ConfirmModal';
import AppointmentForm from './AppointmentForm';
import { useTranslation } from 'react-i18next';
import { AppointmentType } from '../types/AppointmentType';
import WhatsAppApiService from '../services/whatsAppApiService';
import { ToastType, useToast } from '../pages/toast/ToastProvider';
import { WhatsAppMessageTypes } from '../types/WhatsAppMessage';

interface AppointmentEditorProps {
  show: boolean;
  onClose: () => void;
  onSave: (updatedEvent: CalendarEvent) => void;
  onDelete: (appointmentId: string) => void;
  event: CalendarEvent | null;
  appointmentTypes: AppointmentType[] | null;
}

const AppointmentEditorModal: React.FC<AppointmentEditorProps> = ({ show, onClose, onSave, onDelete, event, appointmentTypes }) => {
  const { t, i18n } = useTranslation();
  const [calendarEvent, setCalendarEvent] = useState<CalendarEvent | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const { showToast } = useToast();
  
  useEffect(() => {
    if (event) {
      setCalendarEvent({ ...event });
    }
  }, [event]);

  const handleInputChange = (field: keyof CalendarEvent, value: any) => {
    setCalendarEvent((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSave = () => {
    if (calendarEvent) {
      onSave(calendarEvent);
    }
  };

  const handleDelete = () => {
    setShowConfirm(true);
  };

  const confirmDelete = () => {
    if (calendarEvent?.appointmentId) {
      onDelete(calendarEvent.appointmentId);
    }
    setShowConfirm(false);
  };

  const cancelDelete = () => {
    setShowConfirm(false);
  };

  const getDeleteConfirmationText = () => {
    if (!calendarEvent) return '';
    const name = calendarEvent.name || 'Unnamed';
    const date = calendarEvent.date
      ? format(new Date(calendarEvent.date), 'PPP')
      : 'Unknown Date';
    const time = calendarEvent.time || 'Unknown Time';

    return (
      <>
        {t('are-you-sure-delete-appointment')}
        <br />
        <strong>{t('name')}:</strong> {name}
        <br />
        <strong>{t('date')}:</strong> {date}
        <br />
        <strong>{t('time')}:</strong> {time}
        <br />
        {t('this-action-cannot-be-undone')}
      </>
    );
  };

  if (!calendarEvent) return null;

  const sendReminder = async () => {
    
    if (calendarEvent && calendarEvent?.appointmentId) {
      const controller = new AbortController();

      try {
        const result = await WhatsAppApiService.sendMessage(
          {
            type: WhatsAppMessageTypes.APPOINTMENT_REMINDER,
            data: {
              language: i18n.language === 'he' ? "he" : "en",
              appointmentId: calendarEvent?.appointmentId
            },
          },
          controller.signal
        );

        result.error ? 
          showToast(result.message, ToastType.Warning) 
          : showToast(t(`toastMessages.MESSAGE_SENT_SUCCESS`), ToastType.Success);
      } catch (error: any) {
        showToast(error.response?.data?.message || "Failed to send message", ToastType.Error);
      }
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        size="xl"
        centered
        backdrop="static"
        className="text-light"
        contentClassName="bg-dark"
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{t('edit-appointment')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          <AppointmentForm calendarEvent={calendarEvent} onChange={handleInputChange} appointmentTypes={appointmentTypes} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success"  onClick={sendReminder}>
            Send Reminder
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            {t('delete')}
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {t('save-changes')}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={showConfirm}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        title={t('delete-appointment')}
        body={getDeleteConfirmationText()}
        confirmText={t('delete')}
        cancelText={t('cancel')}
      />
    </>
  );
};

export default AppointmentEditorModal;
